/* eslint-disable @applitools/compat/no-async-await */
import { again, boot } from '@dazn/bootstrap-lib';
import {
    createInstance as createFeaturevisorInstance,
    createLogger,
} from '@featurevisor/sdk';

import { pushToGTM } from '../analytics/handleCommonFeEvent';
import { FeatureFlagCaller } from '../fflag/createFeatureFlagModule';
interface Input {
    environment: boot.config.Environment;
    featurevisorDatafileURL: string;
    trackFeaturevisorErrors: () => boot.featurevisor.VariableValue;
}
type OptimizelyActivationEvent = {
    event: 'optimizelyActivation';
    optimizely: {
        experimentName: string;
        experimentId: string;
        variantName: string;
        variantId: string;
    };
};
const activatedExperiments: string[] = [];

export const createOptimizelyAndFeaturevisorInstances = async ({
    environment,
    featurevisorDatafileURL,
    trackFeaturevisorErrors,
}: Input) => {
    try {
        const featurevisorCb = () => fetch(featurevisorDatafileURL);

        const featureVisorPromise = again(featurevisorCb);

        const [featurevisorData] = (await Promise.all([
            featureVisorPromise.then(res => res.json()),
        ])) as [any];

        let trackError: boot.featurevisor.VariableValue;
        // Featurevisor setup
        const featurevisorInstance = createFeaturevisorInstance({
            datafileUrl: featurevisorDatafileURL,
            handleDatafileFetch: _url => Promise.resolve(featurevisorData),
            onActivation: (featureKey, variationValue, context) => {
                const caller = context?.__caller as FeatureFlagCaller;
                if (
                    (caller === 'activateAnonymous' ||
                        caller === 'activateAuthenticated') &&
                    !activatedExperiments.includes(featureKey)
                ) {
                    activatedExperiments.push(featureKey);
                    pushToGTM(<OptimizelyActivationEvent>{
                        event: 'optimizelyActivation',
                        optimizely: {
                            experimentName: featureKey,
                            experimentId: featureKey,
                            variantName: variationValue,
                            variantId: variationValue,
                        },
                    });
                }
            },
            logger: createLogger({
                levels: ['error', 'warn'],
                handler: function (level, message, details) {
                    if (trackError && level === 'error') {
                        window.newrelic?.addPageAction('FeaturevisorError', {
                            message,
                            ...details,
                        });
                    }
                    console.log('[Featurevisor] ', {
                        level,
                        message,
                        details,
                    });
                },
            }),
            onReady: () => {
                trackError = trackFeaturevisorErrors();
            },
        });

        return {
            featurevisorInstance,
        };
    } catch (err) {
        console.error(
            [
                '[Bootstrap] Featurevisor failed to initialize.',
                `Reason: ${String(err)}`,
            ].join(' '),
        );

        window.newrelic?.noticeError(err as Error, {
            source: 'FeaturevisorInitError',
        });
        return {
            optimizelyInstance: null,
            featurevisorInstance: null,
        };
    }
};
